import React, { useEffect, useState } from "react";
import { Table, Row, Col, Typography, Divider, Tooltip, Empty } from "antd";
import { useLocation } from "react-router-dom";
import RequestHandler from "../../util/RequestHandler";
import moment from "moment";
import "moment/locale/tr";
import { ColumnHelper } from "../../util/TableHelper";
import { RollbackOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { decodeQueryStringParams } from "../../util/QueryStringHelper";

const CashFlowReportDetail = () => {
  const history = useHistory();
  const location = useLocation();
  // Query string'den tüm parametreler
  const queryParams = decodeQueryStringParams(location.search);

  const {
    date,
    currencyId,
    isCalculatedForMonth,
    isTransferred,
    currencyCode,
  } = queryParams;

  const [mainTable, setMainTable] = useState([]);
  const [bankTable, setBankTable] = useState([]);
  const [loadingMain, setLoadingMain] = useState(true);
  const [loadingBank, setLoadingBank] = useState(true);

  useEffect(() => {
    if (!date) return;

    // Sol tablo için API
    RequestHandler.post(
      "/CashFlowReport/GetDetailDataByDate",
      {
        date,
        currencyId,
        isCalculatedForMonth,
        isTransferred,
      },
      false
    ).then((res) => {
      setMainTable(res || []);
      setLoadingMain(false);
    });

    // Sağ tablo için API
    RequestHandler.post(
      "/CashFlowReport/GetBankBalances",
      {
        date,
        currencyId,
        isCalculatedForMonth,
      },
      false
    ).then((res) => {
      setBankTable(res || []);
      setLoadingBank(false);
    });
  }, []);

  const renderDecimal = (value) =>
    value !== null ? ColumnHelper.renderDecimal(value) : null;

  const mainColumns = [
    {
      title: "Cari",
      dataIndex: "name",
      key: "name",
      width: "5%",
      render: (text, record) => {
        if (record.isTotalRow) {
          return <p style={{ fontSize: 15, fontWeight: "bold" }}>{text}</p>;
        } else {
          return text;
        }
      },
    },
    {
      title: "Planlanan Tahsilat",
      dataIndex: "collectPlan",
      key: "collectPlan",
      align: "center",
      render: (text, record) => {
        if (record.isJustUseBalanceRow) {
          return <p></p>;
        } else {
          return renderDecimal(text);
        }
      },
    },
    {
      title: "Gerçekleşen Tahsilat",
      dataIndex: "completedCollect",
      key: "completedCollect",
      align: "center",
      render: (text, record) => {
        if (record.isJustUseBalanceRow) {
          return <p></p>;
        } else {
          return renderDecimal(text);
        }
      },
    },
    {
      title: "Planlanan Ödeme",
      dataIndex: "paymentPlan",
      key: "paymentPlan",
      align: "center",
      render: (text, record) => {
        if (record.isJustUseBalanceRow) {
          return <p></p>;
        } else {
          return renderDecimal(text);
        }
      },
    },
    {
      title: "Gerçekleşen Ödeme",
      dataIndex: "completedPayment",
      key: "completedPayment",
      align: "center",
      render: (text, record) => {
        if (record.isJustUseBalanceRow) {
          return <p></p>;
        } else {
          return renderDecimal(text);
        }
      },
    },
    {
      title: "Fark",
      dataIndex: "difference",
      key: "difference",
      align: "right",
      render: renderDecimal,
    },
  ];

  const bankColumns = [
    {
      title: "Banka",
      dataIndex: "bankLogo",
      key: "bankLogo",
      render: (text, record) => {
        if (record.isTotalRow) {
          return <p style={{ fontSize: 15, fontWeight: "bold" }}>{text}</p>;
        } else {
          return <img src={text}></img>;
        }
      },
      width: "25%",
    },
    {
      title: "Toplam Bakiye",
      dataIndex: "balance",
      key: "balance",
      align: "right",
      render: renderDecimal,
    },
  ];

  const detailItemsTable = (data) => {
    const columns = [
      {
        title: "İşlem Tipi",
        dataIndex: "name",
        key: "name",
        align: "center",
      },
      {
        title: "Tahsilat Planı",
        dataIndex: "collectPlan",
        key: "collectPlan",
        align: "center",
        render: (text, record) => {
          // Eğer bu satır ÖDEME ise (Plan veya Transaction), Tahsilat kolonu boş olsun.
          if (
            record.paymentPlan > 0 ||
            record.completedPayment > 0 ||
            record.isPaymentTransaction
          ) {
            return "";
          }
          // Değilse (Tahsilatsa), 0 ise boş, değilse formatlı göster.
          return renderDecimal(text);
        },
      },
      {
        title: "Gerçekleşen Tahsilat",
        dataIndex: "completedCollect",
        key: "completedCollect",
        align: "center",
        render: (text, record) => {
          // Ödeme satırıysa burayı boş geç
          if (
            record.paymentPlan > 0 ||
            record.completedPayment > 0 ||
            record.isPaymentTransaction
          ) {
            return "";
          }
          return renderDecimal(text);
        },
      },
      {
        title: "Ödeme Planı",
        dataIndex: "paymentPlan",
        key: "paymentPlan",
        align: "center",
        render: (text, record) => {
          // Eğer bu satır TAHSİLAT ise, Ödeme kolonu boş olsun.
          if (
            record.collectPlan > 0 ||
            record.completedCollect > 0 ||
            record.isCollectPlan ||
            record.isCollectTransaction
          ) {
            return "";
          }
          return renderDecimal(text);
        },
      },
      {
        title: "Gerçekleşen Ödeme",
        dataIndex: "completedPayment",
        key: "completedPayment",
        align: "center",
        render: (text, record) => {
          // Tahsilat satırıysa burayı boş geç
          if (
            record.collectPlan > 0 ||
            record.completedCollect > 0 ||
            record.isCollectPlan ||
            record.isCollectTransaction
          ) {
            return "";
          }
          return renderDecimal(text);
        },
      },
    ];

    return (
      <Table
        columns={columns}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Veri Yok"
            />
          ),
        }}
        rowKey={(record) => record.id}
        pagination={false}
        dataSource={data}
        size="small"
      />
    );
  };

  return (
    <div style={{ padding: 20 }}>
      <Row style={{ marginBottom: 9 }}>
        <Col flex={1}>
          <a
            onClick={() => history.goBack()}
            style={{ color: "black", marginLeft: "4%" }}
          >
            {" "}
            <RollbackOutlined /> GERİ DÖN
          </a>
        </Col>
        <Col flex={4}></Col>
      </Row>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <p style={{ fontSize: 20, margin: 0 }}>
          {isCalculatedForMonth
            ? moment(date).locale("tr").format("MMMM YYYY")
            : moment(date).locale("tr").format("D MMMM YYYY")}{" "}
          {isTransferred ? " - Devir " : ""}
          Detay {isCalculatedForMonth ? "(Aylık)" : "(Günlük)"}
        </p>

        <p style={{ fontSize: 20, margin: 0 }}>Para Birimi : {currencyCode}</p>
      </div>

      <Divider></Divider>

      <Row gutter={16}>
        {/* Sol Tablo */}
        <Col span={14}>
          <Table
            columns={mainColumns}
            dataSource={mainTable}
            rowKey={(r, i) => i}
            pagination={false}
            loading={loadingMain}
            expandable={{
              expandedRowRender: (row) => detailItemsTable(row.detailItems),
              rowExpandable: (row) =>
                row.detailItems && row.detailItems?.length > 0,
            }}
          />
        </Col>
        <Col span={2}></Col>
        {/* Sağ Tablo */}
        <Col
          span={8}
          style={{
            border: "1px solid #d9d9d9",
            borderRadius: 6,
            padding: 12,
            marginTop: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 8,
            }}
          >
            <p style={{ fontSize: 20, margin: 0 }}>Banka Listesi</p>
          </div>
          <Table
            columns={bankColumns}
            dataSource={bankTable}
            rowKey={(r, i) => i}
            pagination={false}
            loading={loadingBank}
          />
        </Col>
      </Row>
    </div>
  );
};

export default CashFlowReportDetail;
